import { Form, IFormContext, arraySearch, Modal, IFieldDefns } from 'rewire-ui';
import { EntityType, FieldFilter, IUdf, IValidationRule } from '../DataTypes';
import DataQueries from '../graphql/Queries';
import hoursEarnedTypeDialogModel from '../schedule/HoursEarnedTypeDialogModel';
import { AdvancedAutoCompleteActionType, getAdvancedAutoCompleteOptions } from '../ui/AdvancedAutoComplete';
import { createUdfSearch, getArrayString, getTemplateArrayString } from '../utility/Searchers';
import setViewOnlyForFormFields from '../utility/setViewOnlyForFormFields';
import ValidationRuleEditorModel, { IValidationRuleTable } from './ValidationRuleEditorModel';

const defaultNameSearchReturnSchema = `
  id
  name
  code
  display
`;

type ReferenceType =
  | 'employees'
  | 'batches'
  | 'areas'
  | 'groups'
  | 'occupations'
  | 'reasons'
  | 'generators'
  | 'templates'
  | 'users'
  | 'categories'
  | 'documents'
  | 'hoursEarnedTypes'
  | 'udfs';

interface IValidationRuleEditorInfoPanelModelState {
  isLoading: boolean;
}

class ValidationRuleEditorInfoPanelModel extends Modal<IValidationRuleEditorInfoPanelModelState> {
  validationRuleEditorModel: ValidationRuleEditorModel;
  form: Form;

  constructor(validationRuleEditorModel: ValidationRuleEditorModel) {
    super();

    this.state.isLoading = false;

    this.form = Form.create(
      (_formContext: IFormContext) => ({}),
      {},
      {
        updateOnChange: true,
        validateOnUpdate: false,
      },
    );

    this.validationRuleEditorModel = validationRuleEditorModel;

    this.action('undo', this.undo, { disabled: () => this.viewOnly || (!this.hasChanges() && !this.selectedRule?.isNew) }).action(
      'save',
      this.submit,
      { type: 'submit', icon: 'save', disabled: () => this.viewOnly || (!this.hasChanges() && !this.selectedRule?.isNew) },
    );
  }

  reset() {
    this._reset();
    this.state.isLoading = false;
  }

  _reset() {
    this.form = Form.create(
      (_formContext: IFormContext) => ({}),
      {},
      {
        updateOnChange: true,
        validateOnUpdate: false,
      },
    );
  }

  async load() {
    await (this.state.isLoading = true); // necessary to await this in order for the loader to render properly
    this._reset();
    if (!this.selectedRule?.parameters) {
      this.state.isLoading = false;
      return;
    }

    const parameters = Object.entries(this.selectedRule.parameters).sort(([_key1, p1], [_key2, p2]) => p1.order - p2.order);
    const parametersKeysValues: Record<string, any> = {};
    for (const [key, p] of parameters) {
      parametersKeysValues[key] = p.value;
    }

    this.form.addFields((formContext) => {
      const fields: IFieldDefns = {};
      for (const [parameterName, parameterDefn] of parameters) {
        switch (parameterDefn.type) {
          case 'reference': {
            const referenceType = (parameterDefn as any).typeOptions.referenceType as ReferenceType;
            const options = this.getOptions(referenceType, (parameterDefn as any).typeOptions);
            if (parameterDefn.multiSelect) {
              fields[parameterName] = formContext.multiselectautocomplete(options).label(parameterDefn.name);
            } else {
              fields[parameterName] = formContext.reference(options).label(parameterDefn.name);
            }
            break;
          }

          case 'udf': {
            const udf = this.validationRuleEditorModel.availableUdfs.find((udf: IUdf) => udf.name === parameterName);
            if (!udf) continue;
            fields[parameterName] = this.createUdfField(udf, formContext)!;
            break;
          }

          default:
            if (parameterDefn.typeOptions?.allowableValues) {
              const options = arraySearch(parameterDefn.typeOptions.allowableValues, (i: any) => i || '');
              fields[parameterName] = parameterDefn.multiSelect
                ? formContext.multiselectautocomplete(options).label(parameterDefn.name)
                : formContext.reference(options).label(parameterDefn.name);
            } else if (parameterDefn.type === 'date') {
              fields[parameterName] = formContext.date().label(parameterDefn.name);
            } else if (parameterDefn.type === 'boolean') {
              fields[parameterName] = formContext.switch().label(parameterDefn.name);
            } else if (parameterDefn.type === 'number') {
              fields[parameterName] = formContext.number().label(parameterDefn.name);
            } else {
              fields[parameterName] = formContext.string().label(parameterDefn.name);
            }
            break;
        }
      }

      return fields;
    }, parametersKeysValues);

    setViewOnlyForFormFields(this.form, () => this.viewOnly);

    this.state.isLoading = false;
  }

  createUdfField(udf: IUdf, formContext: IFormContext) {
    switch (udf.type) {
      case 'reference': {
        const options = createUdfSearch(udf) as any;
        const field = formContext.multiselectautocomplete(options).label(udf.displayName);
        return field;
      }

      case 'date':
        return formContext.date().label(udf.displayName);

      case 'text': {
        const options = createUdfSearch(udf, (v) => v.name) as any;
        const field = udf.allowableValues
          ? formContext.multiselectautocomplete(options).label(udf.displayName)
          : formContext.string().label(udf.displayName);
        return field;
      }

      case 'boolean': {
        return formContext.switch().label(udf.displayName);
      }

      case 'integer':
      case 'float':
        return formContext.number().label(udf.displayName);
    }
  }

  get isLoading(): boolean {
    return this.state.isLoading;
  }

  get selectedRule(): IValidationRule | undefined {
    return this.validationRuleEditorModel.selectedRule;
  }

  get selectedRuleTable(): IValidationRuleTable | undefined {
    return this.validationRuleEditorModel.table;
  }

  get viewOnly(): boolean {
    return this.validationRuleEditorModel.viewOnly;
  }

  get canDeleteItem(): boolean {
    return this.validationRuleEditorModel._canDeleteItem;
  }

  protected createReferenceSearch(type: ReferenceType, typeOptions?: any, listMapper?: (v: any) => any) {
    let filter: FieldFilter | undefined = undefined;
    let mapper: (item: any) => any = getArrayString;
    let schema = defaultNameSearchReturnSchema;

    switch (type) {
      case 'categories':
        filter = { field: 'category', eq: this.validationRuleEditorModel.table!.name };
        break;

      case 'hoursEarnedTypes':
        filter = { field: 'type', eq: 'hoursEarnedType' };
        type = 'documents';
        break;

      case 'udfs':
        filter = { field: 'documentType', eq: typeOptions?.documentType ?? 'employees' };
        schema = `
        other(fields: ["displayName", "allowableValues"])
        name
        `;
        listMapper = (v: any) => ({ id: v.name, name: v?.other?.displayName || v.name || '' });
        // mapper = getArrayStringMap((value: any) => (value?.other?.displayName) || value || '');
        break;

      case 'templates':
        mapper = getTemplateArrayString;
        break;
    }

    return {
      async search(search?: string) {
        const r = await DataQueries.getSearch(schema, type as EntityType, search, filter);
        if (r && listMapper) return r.map(listMapper);
        return r;
      },
      map(item: any) {
        if (Array.isArray(item)) return '';
        return mapper(item);
      },
    };
  }

  protected getOptions(type: ReferenceType, typeOptions?: Record<string, unknown>) {
    const search = this.createReferenceSearch(type, typeOptions);
    if (type !== 'hoursEarnedTypes') return search;
    const reasonAction: AdvancedAutoCompleteActionType = { action: this.showHoursEarnedTypeDialog, label: 'Add Hours Earned' };
    return getAdvancedAutoCompleteOptions('Hours Earned', reasonAction, undefined, search);
  }

  showHoursEarnedTypeDialog = () => {
    hoursEarnedTypeDialogModel.load();
  };

  canDelete() {
    return this.selectedRule && this.canDeleteItem;
  }

  deleteRule() {
    if (!this.canDelete()) {
      return;
    }

    this.validationRuleEditorModel.deleteRule(this.selectedRule!);
  }

  hasChanges(): boolean {
    return this.form.hasChanges;
  }

  canUndo() {
    return this.selectedRule && !this.viewOnly;
  }

  undo = () => {
    if (!this.canUndo()) {
      return false;
    }

    if (this.selectedRule!.isNew) {
      this.validationRuleEditorModel.deleteRule(this.selectedRule!);
    } else {
      this.form.revert();
    }

    return false;
  };

  canSave() {
    return this.selectedRule && !this.viewOnly;
  }

  private save = async (): Promise<boolean> => {
    if (!this.canSave()) {
      return false;
    }

    const oldParameters = this.selectedRule!.parameters;
    if (this.selectedRule!.parameters) {
      const formValues = this.form.toObjectValues();
      for (const key of Object.keys(this.selectedRule!.parameters)) {
        if (this.selectedRule!.parameters![key] && Object.prototype.hasOwnProperty.call(formValues, key)) {
          this.selectedRule!.parameters![key].value = formValues[key];
        }
      }
    }

    const result = await this.validationRuleEditorModel.saveRule(this.selectedRule!);
    if (!result) {
      this.selectedRule!.parameters = oldParameters;
    }
    return result;
  };

  submit = async () => {
    if (!this.canSave()) return false;
    await this.form.submitAsync(this.save);
    return false;
  };
}

export default ValidationRuleEditorInfoPanelModel;
